// Pure unit-aware cost rollup for the Current Flask. Mirrors admin's
// basis-of-100g algorithm (apps/admin/client/src/N-Lab/utils/recipeCost.ts)
// extended to handle nos's full AmountUnit enum.
//
// See apps/nos/docs/superpowers/specs/2026-05-17-current-flask-estimated-cost-design.md

import type { FormulationEntry } from "@/utils/types/compound";
import type { AmountUnit } from "@/services/formula_api";

export type CostInputs = {
  pricePerGramUsd: number | null;
  mw: number | null;
  densityGPerMl: number | null;
};

export type CostRowReason = "priced" | "median" | "unpriced" | "no-mw";

export type CostRowDetail = {
  compoundId: string;
  propMass: number | null;
  massFraction: number | null;
  pricePerGramUsd: number | null;
  costUsd: number | null;
  reason: CostRowReason;
};

export type CostBreakdown = {
  /** USD per 100g of finished mix. null when no priced rows exist. */
  usdPer100g: number | null;
  rows: CostRowDetail[];
  knownCount: number;
  medianCount: number;
  unpricedCount: number;
  noMwCount: number;
  totalCompounds: number;
};

export type ComputeCostOptions = {
  useMedianFallback: boolean;
  medianPricePerGramUsd: number | null;
};

// Conversions to a per-100g basis. ppm/ppb/ppt project directly to a mass
// fraction; mass and volume units project to absolute grams (the
// downstream normalization scales them to fractions of Σ propMass).
function propMassFor(
  value: number,
  unit: AmountUnit | undefined,
  mw: number | null,
  densityGPerMl: number | null,
): { propMass: number | null; needsMw: boolean } {
  if (!Number.isFinite(value) || value <= 0) {
    return { propMass: 0, needsMw: false };
  }
  switch (unit) {
    case "ppm": return { propMass: value * 1e-6, needsMw: false };
    case "ppb": return { propMass: value * 1e-9, needsMw: false };
    case "ppt": return { propMass: value * 1e-12, needsMw: false };
    case "mol%":
      if (mw === null) return { propMass: null, needsMw: true };
      return { propMass: (value / 100) * mw, needsMw: false };
    case "mg":   return { propMass: value / 1000, needsMw: false };
    case "g":    return { propMass: value, needsMw: false };
    case "kg":   return { propMass: value * 1000, needsMw: false };
    case "μL":   return { propMass: (value / 1000) * (densityGPerMl ?? 1), needsMw: false };
    case "mL":   return { propMass: value * (densityGPerMl ?? 1), needsMw: false };
    case "L":    return { propMass: value * 1000 * (densityGPerMl ?? 1), needsMw: false };
    default:
      // Unknown / absent unit — treat the row as missing data. Skipped, no
      // contribution.
      return { propMass: null, needsMw: false };
  }
}

export function computeCost(
  formulation: FormulationEntry[],
  costInputs: Map<string, CostInputs>,
  options: ComputeCostOptions,
): CostBreakdown {
  // Pass 1: per-row propMass and noMw detection.
  type Stage1 = {
    compoundId: string;
    propMass: number | null;
    pricePerGramUsd: number | null;
    noMw: boolean;
  };
  const stage: Stage1[] = [];
  let totalPropMass = 0;

  for (const entry of formulation) {
    const ci = costInputs.get(entry.compoundId) ?? {
      pricePerGramUsd: null, mw: null, densityGPerMl: null,
    };
    const { propMass, needsMw } = propMassFor(
      entry.ppm, entry.amountUnit, ci.mw, ci.densityGPerMl,
    );
    if (propMass !== null && propMass > 0) totalPropMass += propMass;
    stage.push({
      compoundId: entry.compoundId,
      propMass,
      pricePerGramUsd: ci.pricePerGramUsd,
      noMw: needsMw,
    });
  }

  // Pass 2: normalize to fractions, pick effective price, compute contribution.
  const rows: CostRowDetail[] = [];
  let usd = 0;
  let knownCount = 0;
  let medianCount = 0;
  let unpricedCount = 0;
  let noMwCount = 0;

  // The divisor caps at max(1, Σ propMass). This means:
  //   - ppm/ppb/ppt-only flasks (Σ < 1): propMass is already a fraction of the
  //     finished mix; remainder is solvent and contributes no cost.
  //   - mol%/g/mL-style flasks (Σ ≥ 1): normalize so the named rows fill the
  //     entire 100g basis.
  //   - Mixed flasks: Σ governs which side dominates, with smooth interpolation.
  const divisor = totalPropMass > 1 ? totalPropMass : 1;

  for (const s of stage) {
    if (s.noMw) {
      noMwCount++;
      rows.push({
        compoundId: s.compoundId, propMass: null, massFraction: null,
        pricePerGramUsd: s.pricePerGramUsd, costUsd: null, reason: "no-mw",
      });
      continue;
    }
    if (s.propMass === null || s.propMass <= 0) {
      // Row contributes no mass (zero value or unparseable unit). Treat it as
      // inert: classify by price status so it still shows up in the "priced"
      // / "median" / "unpriced" tally without skewing the rollup.
      let inertReason: CostRowReason = "priced";
      let inertPrice = s.pricePerGramUsd;
      if (inertPrice === null) {
        if (options.useMedianFallback && options.medianPricePerGramUsd !== null) {
          inertPrice = options.medianPricePerGramUsd;
          inertReason = "median";
          medianCount++;
        } else {
          inertReason = "unpriced";
          unpricedCount++;
        }
      } else {
        knownCount++;
      }
      rows.push({
        compoundId: s.compoundId, propMass: s.propMass, massFraction: 0,
        pricePerGramUsd: inertPrice,
        costUsd: inertReason === "unpriced" ? null : 0,
        reason: inertReason,
      });
      continue;
    }
    const fraction = s.propMass / divisor;
    let effectivePrice = s.pricePerGramUsd;
    let reason: CostRowReason = "priced";
    if (effectivePrice === null) {
      if (options.useMedianFallback && options.medianPricePerGramUsd !== null) {
        effectivePrice = options.medianPricePerGramUsd;
        reason = "median";
      } else {
        unpricedCount++;
        rows.push({
          compoundId: s.compoundId, propMass: s.propMass, massFraction: fraction,
          pricePerGramUsd: null, costUsd: null, reason: "unpriced",
        });
        continue;
      }
    }
    const cost = fraction * effectivePrice * 100;
    usd += cost;
    if (reason === "median") medianCount++;
    else knownCount++;
    rows.push({
      compoundId: s.compoundId, propMass: s.propMass, massFraction: fraction,
      pricePerGramUsd: effectivePrice, costUsd: cost, reason,
    });
  }

  return {
    usdPer100g: knownCount + medianCount > 0 ? usd : null,
    rows,
    knownCount,
    medianCount,
    unpricedCount,
    noMwCount,
    totalCompounds: formulation.length,
  };
}
